/**
 * A plant Grow cannot place, said plainly, and filed with one tap
 * (bloom-backend services/grow/plantNames.js, routes/plants.js).
 *
 * "Grow thinks this is bee balm (Monarda), a perennial flower in the mint
 * family. File it under Perennial flower?" The seed packet reader sends a
 * suggestion with each packet it could not place (ScannedPacket.suggestion),
 * the photo guess says what it saw (TrayGuess latin and family), and "Not in
 * Grow's list? Describe it" asks here.
 *
 * Endpoints (every answer also carries `success`):
 * - POST /plants/identify { text, free? } → { suggestion, asked }. Behind
 *   the photo guess's AI gates: a name Grow knows is answered without a
 *   model; otherwise one model question, counted against the gardener's
 *   allowance like a photo guess (429 AI_LIMIT_*, 402 on a plan without AI;
 *   features/billing/lib/aiLimit). free: never ask a model.
 * - POST /plants/own { name, likePlantId } → { plantId, varietyId, name,
 *   created }: under a broad flower parent a variety of the gardener's own by
 *   that name, under any other plant their own plant in its group.
 *
 * Who uses it: shared/components/plant-fields (DescribePlant,
 * UnknownPlantCard, fileSuggestion), features/packets (PacketReview,
 * acceptSuggestion) and features/photo-tray (PacketPile, PileCard).
 * Caching: none here; filing invalidates the catalogue and the varieties
 * (fileSuggestion). Auth: the signed-in token, in the garden being tended.
 *
 * @module api/plantNames
 */

import { apiClient } from './apiClient';
import { aiDateParams } from './aiAllowance';
import type { PlantKind } from './plantCatalogue';

/** What Grow thinks a plant is, and where it would file it. */
export interface PlantSuggestion {
  /** What it is, as a gardener says it, lower case: "bee balm". */
  name: string;
  /** Its botanical name: "Monarda"; null when not known. */
  latin: string | null;
  /** Its family as a gardener says it: "mint"; null when not known. */
  family: string | null;
  /** The same in Latin: "Lamiaceae"; null when not known. */
  familyLatin: string | null;
  /** The catalogue plant it would be filed under (or is, when known). */
  plantId: number;
  /** That plant's name: "Perennial Flower". */
  plantName: string;
  /** That plant's group: "Garden Flowers Group". */
  groupName: string | null;
  /** That plant's kind. */
  kind: PlantKind | null;
  /** Which broad flower parent it is, when it is one. */
  broad: 'tender' | 'hardy' | 'perennial' | null;
  /** Grow has it already, by another name ("coriander" is Cilantro): filed as that plant. */
  known: boolean;
  /**
   * Only the crop was named ("garlic"), so the plant is filed with Type "Not sure" (B52): plantId is the crop's usual type,
   * which the app does not pick up as a chosen type. Absent or false for a name that names a type.
   */
  typeUnsure?: boolean;
}

/**
 * "Describe it": what Grow makes of a name or a few words.
 *
 * @param text - What the gardener typed.
 * @param free - Only the names Grow knows; never ask a model (nothing is counted).
 * @returns The suggestion (null when Grow could not tell), and whether a model was asked.
 */
export async function identifyPlant(text: string, free = false): Promise<{ suggestion: PlantSuggestion | null; asked: boolean }> {
  const response = await apiClient.post<{ success: boolean; suggestion: PlantSuggestion | null; asked: boolean }>(
    '/plants/identify', { text, ...(free ? { free: true } : {}) }, { params: aiDateParams() },
  );
  return { suggestion: response.data.suggestion ?? null, asked: Boolean(response.data.asked) };
}

/** A plant filed as the gardener's own: the plant to choose and its variety. */
export interface OwnPlantFiled {
  /** Their own plant, or the broad flower parent. */
  plantId: number;
  /** Its "Not sure" variety, or the flower's own variety under the broad parent. */
  varietyId: number;
  /** The name it was filed by: "Bee Balm". */
  name: string;
  /** Made just now (rather than one they had). */
  created: boolean;
}

/**
 * Files a plant Grow does not list under the plant it is most like.
 *
 * @param name - What it is: "bee balm".
 * @param likePlantId - The plant to file it under.
 * @returns The plant and variety to choose.
 */
export async function fileOwnPlant(name: string, likePlantId: number): Promise<OwnPlantFiled> {
  const response = await apiClient.post<OwnPlantFiled & { success: boolean }>('/plants/own', { name, likePlantId });
  const { plantId, varietyId, name: filed, created } = response.data;
  return { plantId, varietyId, name: filed, created };
}
